'use client'
import styles from './Game.module.sass'
import Image from "next/image"
import cn from "classnames"

// Hooks
import {useMemo, useRef} from "react"
import {useGSAP} from "@gsap/react"

// GSAP
import gsap, {SteppedEase} from "gsap"
import SplitText from "gsap/SplitText"
import {ScrollTrigger} from "gsap/ScrollTrigger"

// Components
import SpeakingHead, {SpeakingHeadStyles} from "@/components/SpeakingHead/SpeakingHead"
import Lightbulb from "@/components/Game/Lightbulb/Lightbulb"
import Timeline from "@/components/Game/Timeline/Timeline"
import Compass from "@/components/Game/Compass/Compass"
import Parchment from "@/components/Game/Parchment/Parchment"
import Sword from "@/components/Game/Sword/Sword"
import Chest from "@/components/Game/Chest/Chest"
import Torch from "@/components/Game/Torch/Torch"
import {BackgroundAudio, Objects, SachaState, SpeakingHeadTexts, useGameContext} from "@/context/GameContext"
import SoundButton from "@/components/SoundButton/SoundButton"
import Bag from "@/components/Bag/Bag"
import ScrollIndicator from "@/components/Game/ScrollIndicator/ScrollIndicator";

gsap.registerPlugin(
		SplitText,
		ScrollTrigger,
)


const splitTextWithClass = (textRef, charsClass) => {
	new SplitText(textRef, {types: "chars", charsClass: charsClass});
}

const Game = () => {
	// States
	const {
		gameTimelineRef, isScrollingRef,
		sachaWalkingAnimationRef, sachaJumpingAnimationRef,
		isPlaying, isRestart, sachaState, setSachaState,
		speakingHeadText, setSpeakingHeadText,
		setCurrentAudioBackgroundSrc,
		setPlayAudioOnceSrc, setSecondPlayAudioOneSrc,
		objectsTaken, setObjectsTaken,
		isBackward, setIsBackward,
		setShouldWalk, setAskedSachaState,
		setCurrentBagSection, size, isMobile,
	} = useGameContext()

	// Refs
	const gameAnimationRef = useRef();
	const lightBulbTimelineRef = useRef()
	const isStartLockedRef = useRef(false)
	const scrollIndicatorTimelineRef = useRef()

	const firstTextTimelineRef = useRef()
	const secondTextTimelineRef = useRef()
	const thirdTextTimelineRef = useRef()
	const fourthTextTimelineRef = useRef()
	const fifthTextTimelineRef = useRef()
	const sixthTextTimelineRef = useRef()

	const compassTimelineRef = useRef()
	const parchementTimelineRef = useRef()
	const swordTimelineRef = useRef()
	const chestTimelineRef = useRef()

	const lightBulb_BubbleRef = useRef()
	const firstSpeakingHeadTextRef = useRef()
	const secondSpeakingHeadTextRef = useRef()
	const thirdSpeakingHeadTextRef = useRef()
	const fourthSpeakingHeadTextRef = useRef()
	const fifthSpeakingHeadTextRef = useRef()
	const sixthSpeakingHeadTextRef = useRef()

	const walkingTimeoutRef = useRef()

	const bagApparitionTimelineRef = useRef()

  const jumpingAreas = useMemo( () => [
		{
			firstLabel: 'past_sign',
			firstJumpToLabel: 'first_jump_end_reverse',
			secondLabel: 'second_text',
			secondJumpToLabel: 'first_jump_end',
		},
		{
			firstLabel: 'edge_of_island',
			firstJumpToLabel: 'second_jump_end_reverse',
			secondLabel: 'transition_to_3rd_scene',
			secondJumpToLabel: 'second_jump_end',
		},
		{
			firstLabel: 'edge_of_sword_stage',
			firstJumpToLabel: 'third_jump_end_reverse',
			secondLabel: 'transition_to_5th_scene',
			secondJumpToLabel: 'third_jump_end',
		},
		{
			firstLabel: 'tree_jump',
			firstJumpToLabel: 'fourth_jump_end_reverse',
			secondLabel: 'walking_to_chest',
			secondJumpToLabel: 'fourth_jump_end',
		},
		{
			firstLabel: 'edge_of_chest_stage',
			firstJumpToLabel: 'fifth_jump_end_reverse',
			secondLabel: '6th_scene_enter',
			secondJumpToLabel: 'fifth_jump_end',
		}
	], [])

	const createLightBulbTimeline = () => {
		const bubbleImageWidth = document.querySelector('.Lightbulb__bubble').clientWidth
		const bubbleContainerWidth = document.querySelector('.Lightbulb__lightbulb').clientWidth

		return gsap.timeline({paused: true}).call(() => {
			if(gameTimelineRef.current.time() < .2) isStartLockedRef.current = true
			isMobile && firstTextTimelineRef.current.tweenFromTo(0, 'speaking_heads_text_appeared', {onComplete: () => {
					isStartLockedRef.current = false
					lenis.start()
				}})
		})
		.fromTo('.Lightbulb__bubble', {x:0}, {
			x: -bubbleImageWidth + bubbleContainerWidth,
			ease: SteppedEase.config(3),
			duration: 2,
		})
		// Make the light appear and play the sound
		.fromTo('.Lightbulb__light_bubble', {opacity: 0}, {
			opacity: 1,
			duration: 1,
			onStart: () => {setPlayAudioOnceSrc("/sound/light_bulb.mp3")},
		}, '>')
		.call(() => {
			if(isTimelineGoingForward()) {
				!isMobile && firstTextTimelineRef.current.tweenFromTo(0, 'speaking_heads_text_appeared', {onComplete: () => {
					isStartLockedRef.current = false
					lenis.start()
				}})
			} else {
				!isMobile && fastTweenToEnd(firstTextTimelineRef.current, 'speaking_heads_text_appeared')
			}
		})
		.timeScale(2)
	}
	const createTextTimeline = (speakingHeadClass, charsClass, isDesktop) => {
		// Reset timeline
		 const tl = gsap.timeline().fromTo(speakingHeadClass,{opacity: 0, y: '-150%'}, {opacity: 1, y: '0%', duration: 1}).addLabel('speaking_heads_appeared', '>')
		isDesktop && tl.fromTo(charsClass, {
					opacity: 0,
				}, {
					opacity: 1,
					duration: .01,
					stagger: 0.01,
				}, 'speaking_heads_appeared')
		tl.addLabel('speaking_heads_text_appeared', '>speaking_heads_appeared')
				.to(speakingHeadClass, {
					opacity: 0,
					y: '-150%',
					duration: 1,
				}, 'speaking_heads_text_appeared')
				.pause()
				.timeScale(2)
		return tl
	}

	const createGetObjectTimeline = (object, classObject, classIcon, classSparkle) => {
		const tl = gsap.timeline({paused: true})

		//  Apparition
		tl.addLabel('start')
				.to(classIcon, {opacity: 1, y: '-200%', duration: .5}, 'start')
				.to(classSparkle, {opacity: 1, duration: .25, delay: .25}, 'start')
				.to(classObject, {
					opacity: 0, duration: .5,
				}, 0)
				.addLabel('object_taken')
				// 	Disappear
				.to(classIcon, {y: '-60%', duration: .5}, 'object_taken')
				.to(classSparkle, {opacity: 0, duration: .5}, 'object_taken')

		return tl
	}

	const setTimelineSparklesPosition = (position, isDesktop) => {
		if (isDesktop) {
			gsap.set('.timeline_sparkles_icon', {left: position})
		} else {
			gsap.set('.timeline_sparkles_icon', {top: position})
		}
	}

	const fastTweenToEnd = (textTimeline, label) => {
		textTimeline.tweenTo(label, { duration: .25,
					onComplete: () => {
						textTimeline.tweenTo(100)
					}
				},
		)
	}

	const jumpToPosition = (label) => {
		sachaJumpingAnimationRef.current.tweenFromTo(0, 0.33)
		setSachaState(SachaState.JUMPING)
		setShouldWalk(false)

		let labelTime = gameTimelineRef.current.labels[label];
		const scrollToPosition = Math.ceil((labelTime / gameTimelineRef.current.duration()) * (gameTimelineRef.current.scrollTrigger.end))

		if(isScrollingRef.current) return
		isScrollingRef.current = true
		window.lenis.scrollTo(scrollToPosition, {
			duration: 1.25,
			onComplete: () => {
				isScrollingRef.current = false
			}
		})
	}
	const handleWalkingEnd = (jumpingAreas, self) => {
		const timelineTime = self.animation.time()

		let jumping = false

		jumpingAreas.forEach((area) => {
			const firstLabelTime = self.animation.labels[area.firstLabel];
			const secondLabelTime = self.animation.labels[area.secondLabel];

			if (timelineTime >= firstLabelTime && timelineTime < secondLabelTime) {
				jumping = true
				// If the scroll stops here, scroll to the next point
				if (self.direction > 0) {
					// Get the position of the scrollTo point based on https://gsap.com/community/forums/topic/7410-get-a-labels-position-in-pixels/
					const jumpToLabelTime = self.animation.labels[area.secondJumpToLabel]
					const scrollToPosition = Math.ceil((jumpToLabelTime / self.animation.duration()) * (self.end))
					lenis.scrollTo(scrollToPosition)
				} else {
					const jumpToLabelTime = self.animation.labels[area.firstJumpToLabel]
					const scrollToPosition = Math.floor((jumpToLabelTime / self.animation.duration()) * (self.end))
					lenis.scrollTo(scrollToPosition)
				}
			}
		})

		if (!jumping) {
			sachaWalkingAnimationRef.current.tweenTo(1.75/8, {
				onComplete: () => {
					setAskedSachaState(SachaState.IDLEING)
					sachaWalkingAnimationRef.current.pause()
					lenis.stop()
					lenis.start()
				}
			})
		}
	}

	const createBagTimeline = () => {
		const bagImageWidth = document.querySelector('.gsap-bag_opening').clientWidth
		const bagContainerWidth = document.querySelector('.gsap-bag_opening__container').clientWidth

		const bookImageWidth = document.querySelector('.gsap-book_opening').clientWidth
		const bookContainerWidth = document.querySelector('.gsap-book_opening__container').clientWidth

		const bagZipImageWidth = document.querySelector('.gsap-zip_bag_opening').clientWidth
		const bagZipContainerWidth = document.querySelector('.gsap-bag_opening__container').clientWidth

		return gsap.timeline({paused: true})
				.addLabel('bag_visible', '>game-animation-end')
				// Remove the background music
				.call(() => {
					if (!isTimelineGoingForward()) {
						gsap.set('.gsap-bag', {zIndex: -1, visibility: 'hidden'},)
					}
				})
				.to('.gsap-bag', {
					zIndex: 100,
					visibility: 'visible',
					opacity: 1,
					duration: 1,
				}, 'bag_visible')
				.call(() => {
					if(isTimelineGoingForward()) {
						setPlayAudioOnceSrc("/sound/bag_opening_sound.mp3")
						setSecondPlayAudioOneSrc("/sound/book_pages_sound.mp3")
					}
				})
				.addLabel('bag_appeared', '>bag_visible')
				.to('.gsap-bag_opening', {
					x: -bagImageWidth + bagContainerWidth,
					duration: 1,
					ease: SteppedEase.config(isMobile ? 2 : 4)
				}, 'bag_appeared')
				.call(() => {
					if(!isTimelineGoingForward()) {
						setPlayAudioOnceSrc("/sound/bag_opening_sound.mp3")
					}
				})
				.addLabel('bag_opened', '>bag_appeared')

				// 	Book apparition
				.set('.gsap-book_opening', {opacity: 1}, 'bag_opened')
				.call(() => {
					if(isTimelineGoingForward()) {
						setPlayAudioOnceSrc("/sound/book_opening_sound.mp3")
					}
				})
				.to('.gsap-book_opening', {
					x: -bookImageWidth + bookContainerWidth,
					duration: 2,
					ease: SteppedEase.config(isMobile ? 4 : 10)
				}, 'bag_opened')
				.call(() => {
					if(!isTimelineGoingForward()) {
						setPlayAudioOnceSrc("/sound/book_opening_sound.mp3")
						setSecondPlayAudioOneSrc("/sound/book_pages_sound.mp3")
					}
				})
				.addLabel('book_appeared', '>bag_opened')
				.to('.gsap-book_content', {opacity: 1, duration: 1,}, 'book_appeared')
				.addLabel('book__content_appeared', '>book__content_appeared')
				.call(() => {
					setCurrentBagSection('book')
				}, null, 'book__content_appeared')
				.addLabel('middle')
				.to('.gsap-book_content', {
					opacity: 0,
					duration: 1,
				}, 'book__content_appeared')
				.addLabel('book__content_disappeared', '>book__content_appeared')
				.call(() => {
					if(isTimelineGoingForward()) {
						setPlayAudioOnceSrc("/sound/book_opening_sound.mp3")
						setSecondPlayAudioOneSrc("/sound/book_pages_sound.mp3")
					}
				})
				.to('.gsap-book_opening', {
					x: 0,
					duration: 2,
					ease: SteppedEase.config(isMobile ? 4 : 10)
				}, 'book__content_disappeared')
				.call(() => {
					if(!isTimelineGoingForward()) {
						setPlayAudioOnceSrc("/sound/book_opening_sound.mp3")
					}
				})
				.set('.gsap-book_opening', {opacity: 0}, '>')
				.set('.gsap-book', {display: 'none'}, '>')
				.addLabel('book_disappeared', '>book__content_disappeared')
				.call(() => {
					if(isTimelineGoingForward()) {
						setPlayAudioOnceSrc("/sound/bag_opening_sound.mp3")
					}
				})
				.to('.gsap-bag_opening', {
					x: 0,
					duration: 1,
					ease: SteppedEase.config(isMobile ? 2 : 4)
				}, 'book_disappeared')
				.call(() => {
					if(!isTimelineGoingForward()) {
						setPlayAudioOnceSrc("/sound/bag_opening_sound.mp3")
						setSecondPlayAudioOneSrc("/sound/book_pages_sound.mp3")
					}
				})
				.addLabel('bag_closed', '>book_disappeared')
				.set('.gsap-zip_bag_opening', {visibility: 'visible'}, 'bag_closed')
				.set('.gsap-bag_opening', {visibility: 'hidden'}, 'bag_closed')
				.to('.gsap-zip_bag_opening', {
					x: -bagZipImageWidth + bagZipContainerWidth,
					duration: 2,
					ease: SteppedEase.config(isMobile ? 6 : 24),
				}, 'bag_closed')
				.addLabel('zip_opened', '>bag_closed')
				.to('.gsap-pillars', {
					opacity: 1,
					duration: 1,
				}, 'zip_opened')
				.call(() => {
					setCurrentBagSection('book')
				}, null, 'zip_opened')
				.call(() => {
					setCurrentBagSection('pillars')
				}, null, 'zip_opened')
				.addLabel('pillars_appeared')
	}

	const isTimelineGoingForward = () => {
		return gameTimelineRef.current.scrollTrigger.direction > 0
	}

	// useDebug( {id: 'GSDevTools', animation: 'game_animation', inTime: 0, outTime: 0, timeScale: 1})

	useGSAP(() => {
		//region Game Timeline
		gameTimelineRef.current = gsap.timeline({
			defaults: {
				duration: 1,
				ease: 'none',
			},
			scrollTrigger: {
				// markers: true,
				trigger: gameAnimationRef.current,
				start: 'top top',
				end: 'bottom bottom',
				pin: true,
				scrub: true,
				pinSpacing: false,
				onUpdate: (self) => {
					if(walkingTimeoutRef.current) clearTimeout(walkingTimeoutRef.current)
					if(isStartLockedRef.current && !isMobile) {
						setAskedSachaState(SachaState.IDLEING)
						lenis.stop()
						return
					}

					// Lenis direction
					if (window.lenis.direction === 1) {
						setIsBackward(false)
					} else if (window.lenis.direction === -1){
						setIsBackward(true)
					}

					sachaWalkingAnimationRef.current.repeat(-1).play()
					setAskedSachaState(SachaState.WALKING)

					if(Math.abs(lenis.velocity) < 0.60 && lenis.velocity !== 0) {
						handleWalkingEnd(jumpingAreas, self)
					}

					if (lenis.velocity === 0) {
						walkingTimeoutRef.current = setTimeout(() => {
							handleWalkingEnd(jumpingAreas, self)
						}, 100)
					}
				}
			},
			id: 'game_animation',
		})

		gameTimelineRef.current.pause()

		let mm = gsap.matchMedia(),
				breakPoint = 1025;

		mm.add({
			// set up any number of arbitrarily-named conditions. The function below will be called when ANY of them match.
			isDesktop: `(min-width: ${breakPoint}px)`,
			isMobile: `(max-width: ${breakPoint - 1}px)`,
		}, (context) => {

			let {isDesktop, isMobile} = context.conditions;

			if (isPlaying) {
				setCurrentAudioBackgroundSrc(BackgroundAudio.FIRST_SCENE)
				scrollIndicatorTimelineRef.current = gsap.timeline({repeat: -1})
						.to('.gsap-scroll_indicator', {opacity: .75})
						.to('.gsap-scroll_indicator', {opacity: 1})
			}

			const global_duration = 20
			const BAG_ANIMATION_DURATION = 3
			const moveDuration = (previous_x, new_x, global_duration) => {
				// Calculate the duration of the walk
				return (new_x - previous_x) * global_duration / 100
			}

			lightBulbTimelineRef.current = createLightBulbTimeline()

			compassTimelineRef.current = createGetObjectTimeline(Objects.COMPASS, '.compass', '.timeline_compass_icon', '.timeline_sparkles_icon', isDesktop)
			parchementTimelineRef.current = createGetObjectTimeline(Objects.PARCHMENT, '.parchment__image, .parchment__sparkles', '.timeline_parchment_icon', '.timeline_sparkles_icon',  isDesktop)
			swordTimelineRef.current = createGetObjectTimeline(Objects.SWORD, '.sword__image, .sword__sparkles', '.timeline_sword_icon', '.timeline_sparkles_icon', isDesktop)
			chestTimelineRef.current = createGetObjectTimeline(Objects.CHEST, '.chest__image, .chest__sparkles', '.timeline_chest_icon', '.timeline_sparkles_icon', isDesktop)


			isDesktop && splitTextWithClass(firstSpeakingHeadTextRef.current, "charParent_Start")
			firstTextTimelineRef.current = createTextTimeline('.Start_Head', '.charParent_Start', isDesktop)

			isDesktop && splitTextWithClass(secondSpeakingHeadTextRef.current, "charParent_Second")
			secondTextTimelineRef.current = createTextTimeline('.Second_Head', '.charParent_Second', isDesktop)

			isDesktop && splitTextWithClass(thirdSpeakingHeadTextRef.current, "charParent_Third")
			thirdTextTimelineRef.current = createTextTimeline('.Third_Head', '.charParent_Third', isDesktop)

			isDesktop && splitTextWithClass(fourthSpeakingHeadTextRef.current, "charParent_Fourth")
			fourthTextTimelineRef.current = createTextTimeline('.Fourth_Head', '.charParent_Fourth', isDesktop)

			isDesktop && splitTextWithClass(fifthSpeakingHeadTextRef.current, "charParent_Fifth")
			fifthTextTimelineRef.current = createTextTimeline('.Fifth_Head', '.charParent_Fifth', isDesktop)

			isDesktop && splitTextWithClass(sixthSpeakingHeadTextRef.current, "charParent_Sixth")
			sixthTextTimelineRef.current = createTextTimeline('.Sixth_Head', '.charParent_Sixth', isDesktop)

			//region Game Animation
			//region FIRST SCENE
			gameTimelineRef.current
					.call(() => {
						// Set the text to the first one
						setSpeakingHeadText(SpeakingHeadTexts.FIRST_SCENE)
					})
					.addLabel('start')
					// Make the bubble appear
					.set('.Lightbulb__lightbulb', {opacity: 1}, 'start')
					.call(() => {
						if(scrollIndicatorTimelineRef.current) scrollIndicatorTimelineRef.current.pause()
						gsap.to('.gsap-scroll_indicator', {opacity: 0,duration: 1})

						lightBulbTimelineRef.current.tweenTo(100)
					}, null, 'start+=.01')

					.to({}, {duration: .1})

					.call(() => {
						if(isTimelineGoingForward()) {
							gsap.to('.Lightbulb__lightbulb', {
								opacity: 0,
								duration: 1,
							})
							setShouldWalk(true)
							isMobile && fastTweenToEnd(firstTextTimelineRef.current, 'speaking_heads_text_appeared')
						} else {
							setShouldWalk(false)
							gsap.to('.Lightbulb__lightbulb', {
								opacity: 1,
								duration: 1,
							})
							isMobile && firstTextTimelineRef.current.tweenTo('speaking_heads_text_appeared')
						}
					})

					.addLabel('speaking_heads_text_appeared')

					// Move the background
					.to('.background_member', {
						xPercent: -10,
						duration: moveDuration(0, 10, global_duration),
					}, 'speaking_heads_text_appeared')
					.to('.timeline', {
						'--progression': '10%',
						duration: moveDuration(0, 10, global_duration),
					}, 'speaking_heads_text_appeared')
			isDesktop && (gameTimelineRef.current.to('.gsap-clouds', {xPercent: 5, duration: moveDuration(0, 10, global_duration)}, 'speaking_heads_text_appeared'))
			//endregion

			//region SECOND SCENE
			gameTimelineRef.current.addLabel('2nd_scene_enter')
					// Set the background audios and the speaking head text
					.call( () => {
						if(isTimelineGoingForward()) {
							setCurrentAudioBackgroundSrc(BackgroundAudio.SECOND_SCENE)
							isDesktop && fastTweenToEnd(firstTextTimelineRef.current, 'speaking_heads_text_appeared')
						} else {
							setCurrentAudioBackgroundSrc(BackgroundAudio.FIRST_SCENE)
							setSpeakingHeadText(SpeakingHeadTexts.FIRST_SCENE)
							isDesktop && firstTextTimelineRef.current.tweenTo('speaking_heads_text_appeared')
						}
					})

					// Make the surprise bubble appear
					.to('.surprise_bubble', {
						opacity: 1,
						duration: .5,
					}, '2nd_scene_enter')

					// Walk to the sign
					.to('.background_member', {
						xPercent: -13.5,
						duration: moveDuration(10, 13.5, global_duration),
					}, '2nd_scene_enter')
					.to('.timeline', {
						'--progression': '13.5%',
						duration: moveDuration(10, 13.5, global_duration)
					}, '2nd_scene_enter')
					isDesktop && (gameTimelineRef.current.to('.gsap-clouds', {xPercent: 6.75, duration: moveDuration(10, 13.5, global_duration)}, '2nd_scene_enter'))

				gameTimelineRef.current.addLabel('in_front_of_sign')

					// Walk past the sign
					.to('.background_member', {
						xPercent: -15.5,
						duration: moveDuration(13.5, 15.5, global_duration),
					}, 'in_front_of_sign')
					.to('.timeline', {
						'--progression': '15.5%',
						duration: moveDuration(13.5, 15.5, global_duration)
					}, 'in_front_of_sign')
					isDesktop && (gameTimelineRef.current.to('.gsap-clouds', {xPercent: 7.75, duration: moveDuration(13.5, 15.5, global_duration)}, 'in_front_of_sign'))

					// Set the walk and the text apparition depending on the scroll direction
				gameTimelineRef.current.call(() => {
						if(isTimelineGoingForward()) {
							setSpeakingHeadText(SpeakingHeadTexts.SECOND_SCENE)
							// Jump to position
							jumpToPosition('first_jump_end')
						} else {
							setShouldWalk(true)
						}
					})
					.addLabel('past_sign')
					.addLabel('first_jump_end_reverse', 'past_sign-=.01')

					// Make the bubble disappear
					.to('.surprise_bubble', {opacity: 0, duration: .25}, 'past_sign')

					// Make sacha jump and the background move
					.fromTo('.sacha', {y: 0}, {y: '-23.3%', duration: .5}, 'past_sign')
					.to('.background_member', {
						xPercent: -19,
						duration: moveDuration(15.5, 19, global_duration),
						onReverseComplete: () => {
							setShouldWalk(true)
						}
					}, 'past_sign')
					.to('.timeline', {
						'--progression': '20%',
						duration: moveDuration(15.5, 19, global_duration)
					}, 'past_sign')
					isDesktop && (gameTimelineRef.current.to('.gsap-clouds', {xPercent: 9.5, duration: moveDuration(15.5, 19, global_duration)}, 'past_sign'))
				gameTimelineRef.current.addLabel('second_text')
					.addLabel('first_jump_end', 'second_text+=.1')

					// Set the walk and the text apparition depending on the scroll direction
					.call(() => {
						if(isTimelineGoingForward()) {
							setSachaState(SachaState.IDLEING)
							secondTextTimelineRef.current.tweenFromTo(0,'speaking_heads_text_appeared')
						} else {
							jumpToPosition('first_jump_end_reverse')
							fastTweenToEnd(secondTextTimelineRef.current, 'speaking_heads_text_appeared')
						}
					}, null, 'second_text')

					// Add a pause
					.to({}, {duration: 1})
					.call(() => {if(isTimelineGoingForward()) setPlayAudioOnceSrc("/sound/close_to_object.mp3")}, null, '<')

					// Take the compass
					.addLabel('take_compass')
					.call(() => {
						if(isTimelineGoingForward()) {
							// Play the get item sound - No need to delete the add because it's auto removed (autoRemoveChildren: true)
							compassTimelineRef.current.add(gsap.timeline({autoRemoveChildren: true}).call(() => {setPlayAudioOnceSrc("/sound/get_item_inside_inventory.mp3")}),'start')
							setTimelineSparklesPosition('20%', isDesktop)
							compassTimelineRef.current.tweenFromTo(0, 'object_taken')
							setObjectsTaken([...objectsTaken, Objects.COMPASS])
							setShouldWalk(true)
						} else {
							setPlayAudioOnceSrc("/sound/close_to_object.mp3")
							compassTimelineRef.current.tweenTo(0)
							setObjectsTaken(objectsTaken.filter(item => item !== Objects.COMPASS))
							setShouldWalk(false)
						}
					})
					.addLabel('compass_taken')

					// Move to the edge of the island
					.to('.background_member', {
						xPercent: -21.5,
						duration: moveDuration(19, 21.5, global_duration),
					}, 'compass_taken')
					.to('.timeline', {
						'--progression': '21.5%',
						duration: moveDuration(19, 21.5, global_duration)
					}, 'compass_taken')
					isDesktop && (gameTimelineRef.current.to('.gsap-clouds', {xPercent: 10.75, duration: moveDuration(19, 21.5, global_duration)}, 'compass_taken'))

					// Jump to the 3rd scene transition
				gameTimelineRef.current.call( () => {
						if(isTimelineGoingForward()) {
							jumpToPosition('second_jump_end')
							compassTimelineRef.current.tweenTo(100)
							isMobile && fastTweenToEnd(secondTextTimelineRef.current, 'speaking_heads_text_appeared')
						} else {
							setShouldWalk(true)
							compassTimelineRef.current.tweenTo('object_taken')
							setTimelineSparklesPosition('20%', isDesktop)
							isMobile && secondTextTimelineRef.current.tweenTo('speaking_heads_text_appeared')
						}
					})
					.addLabel('edge_of_island')
					.to('.sacha', {
						y: '-28.7%',
						duration: moveDuration(21.5, 24.5, global_duration),
					}, 'edge_of_island')
					.addLabel('second_jump_end_reverse', 'edge_of_island-=.05')

					// Jump ended
					.call(() => {
						if(isTimelineGoingForward()) {
							setShouldWalk(true)
						} else {
							jumpToPosition('second_jump_end_reverse')
						}
					})
					.to('.background_member', {
						xPercent: -24.5,
						duration: moveDuration(21.5, 24.5, global_duration)
					}, 'edge_of_island')
					.to('.timeline', {
						'--progression': '24.5%',
						duration: moveDuration(21.5, 24.5, global_duration)
					}, 'edge_of_island')
					isDesktop && (gameTimelineRef.current.to('.gsap-clouds', {xPercent: 12.25, duration: moveDuration(21.5, 24.5, global_duration)}, 'edge_of_island'))

				gameTimelineRef.current.addLabel('transition_to_3rd_scene')
					.addLabel('second_jump_end', 'transition_to_3rd_scene+=.1')

					// Remove the texts and the speaking heads
					.call(() => {
						if(isTimelineGoingForward()) {
							isDesktop && fastTweenToEnd(secondTextTimelineRef.current, 'speaking_heads_text_appeared')
						} else {
							isDesktop && secondTextTimelineRef.current.tweenTo('speaking_heads_text_appeared')
						}
					}, null, 'transition_to_3rd_scene+=.5')
					.to('.background_member', {
						xPercent: -31.75,
						duration: moveDuration(24.5, 31.75, global_duration),
					}, 'transition_to_3rd_scene')
					.to('.timeline', {
						'--progression': '31.75%',
						duration: moveDuration(24.5, 31.75, global_duration)
					}, 'transition_to_3rd_scene')
					isDesktop && (gameTimelineRef.current.to('.gsap-clouds', {xPercent: 15.875, duration: moveDuration(24.5, 31.75, global_duration)}, 'transition_to_3rd_scene'))
			//endregion

			//region THIRD SCENE
			gameTimelineRef.current.addLabel('3rd_scene_enter')
					// Set the scene background and stop Sacha
					.call(() => {
						if(isTimelineGoingForward()) {
							setShouldWalk(false)
							setCurrentAudioBackgroundSrc(BackgroundAudio.THIRD_SCENE)
						} else  {
							setShouldWalk(true)
							setCurrentAudioBackgroundSrc(BackgroundAudio.SECOND_SCENE)
							setSpeakingHeadText(SpeakingHeadTexts.SECOND_SCENE)
						}
					})

					// Take the elevator and change
					.to('.elevator', {y: '31vh', duration: 1}, '3rd_scene_enter')
					.to('.sacha', {y: '2.4%', duration: 1}, '3rd_scene_enter')
					.addLabel('elevator_down')

					// Change the text of the speaking head and walk to the parchment
					.call(() => {
						if(isTimelineGoingForward()) {
							setShouldWalk(true)
							setSpeakingHeadText(SpeakingHeadTexts.THIRD_SCENE)
						} else {
							setShouldWalk(false)
						}
					})
					.to('.background_member', {
						xPercent: -35,
						duration: moveDuration(31.75, 35, global_duration),
					}, 'elevator_down')
					.to('.timeline', {
						'--progression': '40%',
						duration: moveDuration(31.75, 35, global_duration)
					}, 'elevator_down')
					isDesktop && (gameTimelineRef.current.to('.gsap-clouds', {xPercent: 17.5, duration: moveDuration(31.75, 35, global_duration)}, 'elevator_down'))

					// Make the text appear
				gameTimelineRef.current.call(() => {
						if(isTimelineGoingForward()) {
							setShouldWalk(false)
							thirdTextTimelineRef.current.tweenFromTo(0, 'speaking_heads_text_appeared')
						} else {
							fastTweenToEnd(thirdTextTimelineRef.current, 'speaking_heads_text_appeared')
							setShouldWalk(true)
						}
					})

					// Add a pause
					.to({}, {duration: 1})
					.call(() => {if(isTimelineGoingForward()) setPlayAudioOnceSrc("/sound/close_to_object.mp3")}, null, '<')

					// Take the parchment
					.addLabel('take_parchment')
					.call(() => {
						if(isTimelineGoingForward()) {
							// Play the get item sound - No need to delete the add because it's auto removed (autoRemoveChildren: true)
							parchementTimelineRef.current.add(gsap.timeline({autoRemoveChildren: true}).call(() => {setPlayAudioOnceSrc("/sound/get_item_inside_inventory.mp3")}),'start')
							setTimelineSparklesPosition('40%', isDesktop)
							parchementTimelineRef.current.tweenFromTo(0, 'object_taken')
							setObjectsTaken([...objectsTaken, Objects.PARCHMENT])
							setShouldWalk(true)
						} else {
							setPlayAudioOnceSrc("/sound/close_to_object.mp3")

							parchementTimelineRef.current.tweenTo(0)
							setObjectsTaken(objectsTaken.filter(item => item !== Objects.PARCHMENT))
							setShouldWalk(false)
						}
					})
					.to('.elevator', {y: '0vh', duration: .4, delay: .2}, 'take_parchment')
					.addLabel('parchment_taken')
					.call(() => {
						if(isTimelineGoingForward()) {
							parchementTimelineRef.current.tweenTo(100)
							isMobile && fastTweenToEnd(thirdTextTimelineRef.current, 'speaking_heads_text_appeared')
						} else {
							setShouldWalk(true)
							parchementTimelineRef.current.tweenTo('object_taken')
							setTimelineSparklesPosition('40%', isDesktop)
							isMobile && thirdTextTimelineRef.current.tweenTo('speaking_heads_text_appeared')
						}
					}, null, 'parchment_taken+=.5')

					// Walk to the next scene
					.to('.background_member', {
						xPercent: -45,
						duration: moveDuration(35, 45, global_duration),
					}, 'parchment_taken')
					.to('.timeline', {
						'--progression': '50%',
						duration: moveDuration(35, 45, global_duration)
					}, 'parchment_taken')
					isDesktop && (gameTimelineRef.current.to('.gsap-clouds', {xPercent: 22.5, duration: moveDuration(35, 45, global_duration)}, 'parchment_taken'))

					// Remove the texts and the speaking heads
				gameTimelineRef.current.call(() => {
						if(isTimelineGoingForward()) {
							isDesktop && fastTweenToEnd(thirdTextTimelineRef.current, 'speaking_heads_text_appeared')
						} else {
							isDesktop && thirdTextTimelineRef.current.tweenTo('speaking_heads_text_appeared')
						}
					}, null, 'parchment_taken+=1.5')
			//endregion

			//region FOURTH SCENE
			gameTimelineRef.current.addLabel('4th_scene_enter')
					// Set the scene background
					.call(() => {
						if(isTimelineGoingForward()) {
							setCurrentAudioBackgroundSrc(BackgroundAudio.FOURTH_SCENE)
						} else {
							setSpeakingHeadText(SpeakingHeadTexts.THIRD_SCENE)
							setCurrentAudioBackgroundSrc(BackgroundAudio.THIRD_SCENE)
						}
					})

					// Walk to the sword stage (small fall)
					.set('.sacha', {y: '-25%'}, '4th_scene_enter')
					.to('.sacha', {
						y: '-18.6%',
						duration: moveDuration(45, 47, global_duration)
					}, '4th_scene_enter')
					.to('.background_member', {
						xPercent: -47,
						duration: moveDuration(45, 47, global_duration)
					}, '4th_scene_enter')
					.to('.timeline', {
						'--progression': '55%',
						duration: moveDuration(45, 47, global_duration)
					}, '4th_scene_enter')
					isDesktop && (gameTimelineRef.current.to('.gsap-clouds', {xPercent: 23.5, duration: moveDuration(45, 47, global_duration)}, '4th_scene_enter'))

					// Walk to the sword
				gameTimelineRef.current.addLabel('on_sword_stage')
					.to('.background_member', {
						xPercent: -51.5,
						duration: moveDuration(47, 51.5, global_duration),
					}, 'on_sword_stage')
					.to('.timeline', {
						'--progression': '60%',
						duration: moveDuration(47, 51.5, global_duration)
					}, 'on_sword_stage')
					isDesktop && (gameTimelineRef.current.to('.gsap-clouds', {xPercent: 25.75, duration: moveDuration(47, 51.5, global_duration)}, 'on_sword_stage'))

					// Make the text appear
				gameTimelineRef.current.call(() => {
						if(isTimelineGoingForward()) {
							setSpeakingHeadText(SpeakingHeadTexts.FOURTH_SCENE)
							setShouldWalk(false)
							fourthTextTimelineRef.current.tweenFromTo(0, 'speaking_heads_text_appeared')
						} else {
							fastTweenToEnd(fourthTextTimelineRef.current, 'speaking_heads_text_appeared')
							setShouldWalk(true)
						}
					})

					// Add a pause
					.to({}, {duration: 1})
					.call(() => {if(isTimelineGoingForward()) setPlayAudioOnceSrc("/sound/close_to_object.mp3")}, null, '<')

					// Take the sword
					.addLabel('take_sword')
					.call(() => {
						if(isTimelineGoingForward()) {
							// Play the get item sound - No need to delete the add because it's auto removed (autoRemoveChildren: true)
							swordTimelineRef.current.add(gsap.timeline({autoRemoveChildren: true}).call(() => {setPlayAudioOnceSrc("/sound/get_item_inside_inventory.mp3")}),'start')
							setTimelineSparklesPosition('60%', isDesktop)
							swordTimelineRef.current.tweenFromTo(0, 'object_taken')
							setObjectsTaken([...objectsTaken, Objects.SWORD])
							setShouldWalk(true)
						} else {
							setPlayAudioOnceSrc("/sound/close_to_object.mp3")

							swordTimelineRef.current.tweenTo(0)
							setObjectsTaken(objectsTaken.filter(item => item !== Objects.SWORD))
							setShouldWalk(false)
						}
					})

					// Walk to the stage edge
					.addLabel('sword_taken')
					.to('.background_member', {
						xPercent: -58,
						duration: moveDuration(51.5, 58, global_duration),
					}, 'sword_taken')
					.to('.timeline', {
						'--progression': '67%',
						duration: moveDuration(51.5, 58, global_duration)
					}, 'sword_taken')
					isDesktop && (gameTimelineRef.current.to('.gsap-clouds', {xPercent: 29, duration: moveDuration(51.5, 58, global_duration)}, 'sword_taken'))
				gameTimelineRef.current.call(() => {
						if(isTimelineGoingForward()) {
							swordTimelineRef.current.tweenTo(100)
							isMobile && fastTweenToEnd(fourthTextTimelineRef.current, 'speaking_heads_text_appeared')
						} else {
							swordTimelineRef.current.tweenTo('object_taken')
							setTimelineSparklesPosition('60%', isDesktop)
							isMobile && fourthTextTimelineRef.current.tweenTo('speaking_heads_text_appeared')
						}
					}, null, 'sword_taken+=.5')
					.addLabel('edge_of_sword_stage')
					.addLabel('third_jump_end_reverse', 'edge_of_sword_stage-=.01')

					// Jump to the fifth scene
					.call(() => {
						if(isTimelineGoingForward()) {
							jumpToPosition('third_jump_end')
							isDesktop && fastTweenToEnd(fourthTextTimelineRef.current, 'speaking_heads_text_appeared')
						} else {
							isDesktop && fourthTextTimelineRef.current.tweenTo('speaking_heads_text_appeared')
							setShouldWalk(true)
						}
					})
					.to('.sacha', {
						y: '-30%',
						duration: moveDuration(58, 61, global_duration) * .6
					}, 'edge_of_sword_stage')
					.to('.sacha', {
						y: '-25.7%',
						duration: moveDuration(58, 61, global_duration) * .4,
						delay: moveDuration(58, 61, global_duration) * .6
					}, 'edge_of_sword_stage')
					.to('.background_member', {
						xPercent: -61,
						duration: moveDuration(58, 61, global_duration),
					}, 'edge_of_sword_stage')
					.to('.timeline', {
						'--progression': '70%',
						duration: moveDuration(58, 61, global_duration)
					}, 'edge_of_sword_stage')
					isDesktop && (gameTimelineRef.current.to('.gsap-clouds', {xPercent: 30.5, duration: moveDuration(58, 61, global_duration)}, 'edge_of_sword_stage'))
			//endregion

			// region FIFTH SCENE
			gameTimelineRef.current.addLabel('transition_to_5th_scene')
					.addLabel('third_jump_end', 'transition_to_5th_scene+=.01')
					// Set the scene background and the reverse jump
					.call(() => {
						if(isTimelineGoingForward()) {
							setShouldWalk(true)
							setCurrentAudioBackgroundSrc(BackgroundAudio.FIFTH_SCENE)
						} else {
							jumpToPosition('third_jump_end_reverse')
							setSpeakingHeadText(SpeakingHeadTexts.FOURTH_SCENE)
							setCurrentAudioBackgroundSrc(BackgroundAudio.FOURTH_SCENE)
						}
					})

					// Walk to the tree
					.to('.background_member', {
						xPercent: -62,
						duration: moveDuration(61, 62, global_duration),
					}, 'transition_to_5th_scene')
					.to('.timeline', {
						'--progression': '71%',
						duration: moveDuration(61, 62, global_duration)
					}, 'transition_to_5th_scene')
					isDesktop && (gameTimelineRef.current.to('.gsap-clouds', {xPercent: 31, duration: moveDuration(61, 62, global_duration)}, 'transition_to_5th_scene'))

					// Jump to the chest stage
				gameTimelineRef.current.addLabel('tree_jump')
					.call(() => {
						if(isTimelineGoingForward()) {
							jumpToPosition('fourth_jump_end')
						} else {
							setShouldWalk(true)
						}
					})
					.to('.sacha', {
						y: '-32%',
						duration: moveDuration(62, 65, global_duration) * .4
					}, 'tree_jump')
					.to('.sacha', {
						y: '-12%',
						duration: moveDuration(62, 65, global_duration) * .6,
						delay: moveDuration(62, 65, global_duration) * .4
					}, 'tree_jump')
					.to('.background_member', {
						xPercent: -65,
						duration: moveDuration(62, 65, global_duration),
					}, 'tree_jump')
					.to('.timeline', {
						'--progression': '75%',
						duration: moveDuration(62, 65, global_duration)
					}, 'tree_jump')
					isDesktop && (gameTimelineRef.current.to('.gsap-clouds', {xPercent: 32.5, duration: moveDuration(62, 65, global_duration)}, 'tree_jump'))
				gameTimelineRef.current.addLabel('fourth_jump_end_reverse', 'tree_jump-=.01')

					// Walk to the chest
					.call(() => {
						if(isTimelineGoingForward()) {
							setSpeakingHeadText(SpeakingHeadTexts.FIFTH_SCENE)
							setShouldWalk(true)
						} else {
							jumpToPosition('fourth_jump_end_reverse')
						}
					})
					.addLabel('walking_to_chest')
					.to('.background_member', {
						xPercent: -67,
						duration: moveDuration(65, 67, global_duration),
					}, 'walking_to_chest')
					.to('.timeline', {
						'--progression': '80%',
						duration: moveDuration(65, 67, global_duration)
					}, 'walking_to_chest')
					isDesktop && (gameTimelineRef.current.to('.gsap-clouds', {xPercent: 33.5, duration: moveDuration(65, 67, global_duration)}, 'walking_to_chest'))
				gameTimelineRef.current.addLabel('fourth_jump_end', 'walking_to_chest+=.01')

					// Make the text appear
					.call(() => {
						if(isTimelineGoingForward()) {
							setShouldWalk(false)
							fifthTextTimelineRef.current.tweenFromTo(0, 'speaking_heads_text_appeared')
						} else {
							fastTweenToEnd(fifthTextTimelineRef.current, 'speaking_heads_text_appeared')
							setShouldWalk(true)
						}
					})

					// Add a pause
					.to({}, {duration: 1})
					.call(() => {if(isTimelineGoingForward()) setPlayAudioOnceSrc("/sound/close_to_object.mp3")}, null, '<')

					// Take the chest
					.addLabel('take_chest')
					.call(() => {
						if(isTimelineGoingForward()) {
							// Play the get item sound - No need to delete the add because it's auto removed (autoRemoveChildren: true)
							chestTimelineRef.current.add(gsap.timeline({autoRemoveChildren: true}).call(() => {setPlayAudioOnceSrc("/sound/get_item_inside_inventory.mp3")}),'start')
							setTimelineSparklesPosition('80%', isDesktop)

							chestTimelineRef.current.tweenFromTo(0, 'object_taken')
							setObjectsTaken([...objectsTaken, Objects.CHEST])
							setShouldWalk(true)
						} else {
							setPlayAudioOnceSrc("/sound/close_to_object.mp3")

							chestTimelineRef.current.tweenTo(0)
							setObjectsTaken(objectsTaken.filter(item => item !== Objects.CHEST))
							setShouldWalk(false)
						}
					})

					//  Walk to the edge of the stage
					.addLabel('chest_taken')
					.call(() => {
						if(isTimelineGoingForward()) {
							chestTimelineRef.current.tweenTo(100)
							isMobile && fastTweenToEnd(fifthTextTimelineRef.current, 'speaking_heads_text_appeared')
						} else {
							chestTimelineRef.current.tweenTo('object_taken')
							setTimelineSparklesPosition('80%', isDesktop)
							isMobile && fifthTextTimelineRef.current.tweenTo('speaking_heads_text_appeared')
						}
					}, null, 'chest_taken+=.5')
					.to('.background_member', {
						xPercent: -72.5,
						duration: moveDuration(66, 72.5, global_duration),
						onStart: () => {
							setShouldWalk(true)
						},
						onReverseComplete: () => {
							setShouldWalk(false)
						},
					}, 'chest_taken')
					.to('.timeline', {
						'--progression': '85%',
						duration: moveDuration(66, 72.5, global_duration)
					}, 'chest_taken')
					isDesktop && (gameTimelineRef.current.to('.gsap-clouds', {xPercent: 36.25, duration: moveDuration(66, 72.5, global_duration)}, 'chest_taken'))

					// Jump to the next scene
				gameTimelineRef.current.addLabel('edge_of_chest_stage')
					.addLabel('fifth_jump_end_reverse', 'edge_of_chest_stage-=.01')
					.call(() => {
						if(isTimelineGoingForward()) {
							jumpToPosition('fifth_jump_end')
							isDesktop && fastTweenToEnd(fifthTextTimelineRef.current, 'speaking_heads_text_appeared')
						} else {
							setSpeakingHeadText(SpeakingHeadTexts.FIFTH_SCENE)
							isDesktop && fifthTextTimelineRef.current.tweenTo('speaking_heads_text_appeared')
							setShouldWalk(true)
						}
					})
					.to('.sacha', {
						y: '-20%',
						duration: moveDuration(72.5, 75, global_duration) * .4
					}, 'edge_of_chest_stage')
					.to('.sacha', {
						y: '-9.5%',
						duration: moveDuration(72.5, 75, global_duration) * .4,
						delay: moveDuration(72.5, 75, global_duration) * .6
					}, 'edge_of_chest_stage')
					.to('.background_member', {
						xPercent: -77,
						duration: moveDuration(72.5, 77, global_duration),
					}, 'edge_of_chest_stage')
					.to('.timeline', {
						'--progression': '90%',
						duration: moveDuration(72.5, 77, global_duration)
					}, 'edge_of_chest_stage')
					isDesktop && (gameTimelineRef.current.to('.gsap-clouds', {xPercent: 38.5, duration: moveDuration(72.5, 77, global_duration)}, 'edge_of_chest_stage'))
				gameTimelineRef.current.call(() => {
						if(isTimelineGoingForward()) {
							setShouldWalk(true)
						} else {
							jumpToPosition('fifth_jump_end_reverse')
						}
					})
			//endregion

			// region SIXTH SCENE
			gameTimelineRef.current.addLabel('6th_scene_enter')
					.addLabel('fifth_jump_end', '6th_scene_enter+=.01')
					// Set the scene background audio and move Sacha
					.set('.sacha', {y: '-4.7%'}, '6th_scene_enter')
					.call(() => {
						if(isTimelineGoingForward()) {
							setCurrentAudioBackgroundSrc(BackgroundAudio.FIRST_SCENE)
						} else {
							setCurrentAudioBackgroundSrc(BackgroundAudio.FIFTH_SCENE)
						}
					})
					.to('.background_member', {
						xPercent: -82,
						duration: moveDuration(77, 82, global_duration),
					}, '6th_scene_enter')
					.to('.timeline', {
						'--progression': '95%',
						duration: moveDuration(77, 82, global_duration)
					}, '6th_scene_enter')
					isDesktop && (gameTimelineRef.current.to('.gsap-clouds', {xPercent: 41, duration: moveDuration(77, 82, global_duration)}, '6th_scene_enter'))
				gameTimelineRef.current.addLabel('walk_to_end')


			if (isDesktop) {
				gameTimelineRef.current.to('.sacha', {xPercent: 5, duration: 2}, 'walk_to_end')
			} else {
				gameTimelineRef.current.to('.background_member', {xPercent: -87, duration: 2}, 'walk_to_end')
			}

			gameTimelineRef.current.to('.timeline', {'--progression': '100%', duration: 2}, 'walk_to_end')
					isDesktop && (gameTimelineRef.current.to('.gsap-clouds', {xPercent: 43.5, duration: 2}, 'walk_to_end'))
			gameTimelineRef.current.call(() => {
						if(isTimelineGoingForward()) {
							setSpeakingHeadText(SpeakingHeadTexts.SIXTH_SCENE)
							isDesktop && sixthTextTimelineRef.current.tweenFromTo(0, 'speaking_heads_text_appeared')
						} else {
							isDesktop && fastTweenToEnd(sixthTextTimelineRef.current, 'speaking_heads_text_appeared')
						}
					}, null, 'walk_to_end')
					.addLabel('game-animation-end')
					.call(() => {
						if(isTimelineGoingForward()) {
							setShouldWalk(false)
							isMobile && sixthTextTimelineRef.current.tweenFromTo(0, 'speaking_heads_text_appeared')
						} else {
							setShouldWalk(true)
							isMobile && fastTweenToEnd(sixthTextTimelineRef.current, 'speaking_heads_text_appeared')
						}
					})
			//endregion
			//endregion

			//region Bag Animation
			.to({}, {duration: 1}, 'game-animation-end')
			.call(() => {
				if (isTimelineGoingForward()) {
					setCurrentAudioBackgroundSrc(BackgroundAudio.NO_SOUND)
				} else {
					setCurrentAudioBackgroundSrc(BackgroundAudio.FIRST_SCENE)
				}
			})
			.addLabel('bag_animation')

			bagApparitionTimelineRef.current = createBagTimeline()

			gameTimelineRef.current
				.call(() => {
					if(isTimelineGoingForward()) {
						// Progress the timeline
						bagApparitionTimelineRef.current.tweenTo('middle', {duration: BAG_ANIMATION_DURATION})
					} else {
						bagApparitionTimelineRef.current.tweenTo(0, {duration: BAG_ANIMATION_DURATION})
					}
				}, null, 'bag_animation')
				.addLabel('book__content_appeared')
				.to({}, {duration: 2}, 'bag_animation')

			gameTimelineRef.current
					.call(() => {
						if(isTimelineGoingForward()) {
							bagApparitionTimelineRef.current.tweenTo('pillars_appeared', {duration: BAG_ANIMATION_DURATION})
						} else {
							bagApparitionTimelineRef.current.tweenTo('middle', {duration: BAG_ANIMATION_DURATION})
						}
					}, null, 'book_section_end')
					.to({}, {duration: 2})
					.addLabel('pillars_appeared')
			//endregion

		})
		//endregion
	}, {
		dependencies: [
			isPlaying,
			isRestart,
			size.width,
		],
		scope: gameAnimationRef,
		revertOnUpdate: true,
	})

	return (
			<section className={cn(styles.game__wrapper, 'gsap-scroll_container')}
			         ref={gameAnimationRef} id={'game'}>
				<div
						className={styles.game}
				>
					{/*Scroll Indicator*/}
					<ScrollIndicator className={styles.scroll_indicator}/>

					{/*region Background*/}

					{/*region Hearts*/}
					<div className={styles.hearts}>
						<Image src="/Global/SingleHeart.png"
						       alt="Heart"
						       width={80}
						       height={80}
						       className={styles.heart}/>
						<Image src="/Global/SingleHeart.png"
						       alt="Heart"
						       width={80}
						       height={80}
						       className={styles.heart}/>
						<Image src="/Global/SingleHeart.png"
						       alt="Heart"
						       width={80}
						       height={80}
						       className={styles.heart}/>
						<Image src="/Global/SingleHeart.png"
						       alt="Heart"
						       width={80}
						       height={80}
						       className={styles.heart}/>
					</div>
					{/*endregion*/}

					{/*region Speaking Heads*/}
					<div className={cn(styles.speaking_heads, 'speaking_heads', {[styles.last_text]: speakingHeadText === SpeakingHeadTexts.SIXTH_SCENE})}>
						<SpeakingHead className={{[styles.current]: speakingHeadText === SpeakingHeadTexts.FIRST_SCENE}}
						              gsapClassName={'Start_Head'} parentStyle={styles}
						              speaker={SpeakingHeadStyles.SACHA} scrollToSeeMore={true}>
							<span className={styles.text} ref={firstSpeakingHeadTextRef}>Je veux monter ma boîte, à moi l'aventure entrepreneuriale ! Mais les possibilités sont infinies. Les statuts ? Le financement ? Par où commencer ? En route vers l'inconnu !</span>
						</SpeakingHead>
						<SpeakingHead className={{[styles.current]: speakingHeadText === SpeakingHeadTexts.SECOND_SCENE}}
						              gsapClassName={'Second_Head'} parentStyle={styles}
						              speaker={SpeakingHeadStyles.LAWYER} scrollToSeeMore={true}>
							<span className={styles.text} ref={secondSpeakingHeadTextRef}>Bonjour, je suis maître affectio. Avec cette boussole je m’engage à vos côtés pour vous accompagner vous et votre entreprise. Adaptez votre cap selon vos besoins, elle sera votre guide vers la réussite !</span>
						</SpeakingHead>
						<SpeakingHead className={{[styles.current]: speakingHeadText === SpeakingHeadTexts.THIRD_SCENE}}
						              gsapClassName={'Third_Head'} parentStyle={styles}
						              speaker={SpeakingHeadStyles.LAWYER} scrollToSeeMore={true}>
							<span className={styles.text} ref={thirdSpeakingHeadTextRef}>Ce parchemin incarne la création des statuts de votre entreprise ainsi que notre collaboration pour la rédaction et la négociation de vos contrats commerciaux, la conformité à des réglementations.</span>
						</SpeakingHead>
						<SpeakingHead className={{[styles.current]: speakingHeadText === SpeakingHeadTexts.FOURTH_SCENE}}
						              gsapClassName={'Fourth_Head'} parentStyle={styles}
						              speaker={SpeakingHeadStyles.LAWYER} scrollToSeeMore={true}>
							<span className={cn(styles.text, {[styles.current]: speakingHeadText === SpeakingHeadTexts.FOURTH_SCENE})} ref={fourthSpeakingHeadTextRef}>Une bataille juridique en vue ? Grâce à cette lame, vous venez d'acquérir le pouvoir de négociation de votre avocat. Mes armes : les arguments légaux, les droits de l’entrepreneur… Je vous défendrai et protégerai vos intérêts.</span>
						</SpeakingHead>
						<SpeakingHead className={{[styles.current]: speakingHeadText === SpeakingHeadTexts.FIFTH_SCENE}}
						              gsapClassName={'Fifth_Head'} parentStyle={styles} speaker={SpeakingHeadStyles.LAWYER} scrollToSeeMore={true}>
							<span className={cn(styles.text, {[styles.current]: speakingHeadText === SpeakingHeadTexts.FIFTH_SCENE})} ref={fifthSpeakingHeadTextRef}>Ce trésor renferme votre prospérité économique : des stratégies fiscales avancées, des aides pour les levées de fonds, des opportunités d'optimisation financière qui façonneront la croissance durable de votre entreprise.</span>
						</SpeakingHead>
						<SpeakingHead className={{[styles.current]: speakingHeadText === SpeakingHeadTexts.SIXTH_SCENE}}
						              gsapClassName={'Sixth_Head'} parentStyle={styles}
						              speaker={SpeakingHeadStyles.LAWYER} scrollToSeeMore={true}>
							<span className={cn(styles.text, {[styles.current]: speakingHeadText === SpeakingHeadTexts.SIXTH_SCENE})} ref={sixthSpeakingHeadTextRef}>Bravo ! Votre courage et détermination seront des atouts indispensables pour vos futurs défis !  N'oubliez pas : un accompagnement juridique est essentiel pour triompher dans le monde des affaires. À vous de jouer !</span>
						</SpeakingHead>
					</div>
					{/*endregion*/}

					{/*region Scene*/}
					<div className={styles.scene}>
						{/*region Plans*/}
						<div className={cn(styles.first_plan, 'background_member')} id={'first_plan'}>
							<Image src={'/images/Game/Plan/first_plan.webp'}
							       alt="First Plan"
							       width={2767}
							       height={256}
							       className={styles.first_plan__image}
							       unoptimized={true}/>
						</div>

						<div className={cn(styles.background, 'background_member')}>
							<Image src={'/images/Game/Plan/2nd_plan.webp'}
							       alt="Second Plan"
							       width={2767}
							       height={256}
							       className={styles.second}
							       unoptimized={true}/>
							<Image src={'/images/Game/Plan/3rd_plan.webp'}
							       alt="Third Plan"
							       width={2767}
							       height={256}
							       className={styles.third}
							       unoptimized={true}/>
							<Image src={'/images/Game/Plan/cave.png'}
							       alt="Third Plan"
							       width={512}
							       height={256}
							       className={styles.cave}
							       unoptimized={true}/>
						</div>

						<div className={cn(styles.cloud__wrapper, 'background_member')}>
							<div className={cn(styles.cloud, 'gsap-clouds')}></div>
						</div>
						{/*endregion*/}

						<div className={cn(styles.fixed_items, 'background_member')}>
							{/*region First Scene*/}
							{/*Cat*/}
							{isMobile ? <Image className={styles.cat}
							                   src={'/images/Game/Cat/cat.png'}
							                   alt={"Cat Idle"}
							                   width={70}
							                   height={40}
							                   unoptimized={true}/>
									: <Image className={styles.cat}
									         src={'/images/Game/Cat/cat.gif'}
									         alt={"Cat Idle"}
									         width={70}
									         height={40}/>}
							{/*LightBulb*/}
							<Lightbulb className={cn(styles.light_bulb)} ref={lightBulb_BubbleRef}/>
							{/*endregion*/}

							{/*region Second Scene*/}
							<Compass parentStyle={styles} className={cn(styles.compass, 'compass')}/>
							{/*endregion*/}

							{/*region Third Scene*/}
							{/*Elevator*/}
							<div className={cn(styles.elevator)}>
								<Image className={cn(styles.elevator__image, 'elevator')}
								       src={'/images/Game/Library_Scene/elevator.png'}
								       alt={"Elevator"}
								       width={580}
								       height={72}/>
							</div>

							{/*Parchment*/}
							<Parchment parentStyle={styles} className={cn(styles.parchment, 'parchment')}/>
							{/*endregion*/}

							{/*region Fourth Scene*/}
							<Sword parentStyle={styles} className={cn(styles.sword, 'sword')}/>
							<Torch parentStyle={styles} className={cn(styles.torch)}/>
							{/*endregion*/}

							{/*region Fifth Scene*/}
							<Chest parentStyle={styles} className={cn(styles.chest, 'chest')}/>
							{/*endregion*/}

							{/*region Sixth Scene*/}
							<div className={cn(styles.lawyer, 'lawyer')}>
								{isMobile ? <Image className={styles.lawyer__gif}
								                   src={'/images/Game/Lawyer/lawyer.png'}
								                   alt={"Lawyer"}
								                   width={64}
								                   height={64}
								                   unoptimized={true}/>
										: <Image className={styles.lawyer__gif}
										         src={'/images/Game/Lawyer/lawyer.gif'}
										         alt={"Lawyer"}
										         width={64}
										         height={64}/>}
							</div>
							{/*endregion*/}
						</div>

						{/*region Sacha*/}
						<div className={cn(styles.sacha, 'sacha')}>
							{/*Surprise bubble*/}
							<div className={cn(styles.surprise_bubble, 'surprise_bubble')}>
								<Image src={'/images/Game/Bubble/surpriseBubble.png'}
								       className={styles.surprise_bubble__image}
								       alt="Surprise Bubble"
								       width={272}
								       height={272}/>
							</div>

							{/*Sacha states*/}
							<div className={cn(styles.sacha__wrapper, {[styles.backward]: isBackward}, 'sacha__wrapper')}
							     id={'sacha'}>
								{isMobile !== undefined ? (isMobile
												?
												<Image className={cn(styles.idleing, {[styles.current]: sachaState === SachaState.IDLEING}, 'gsap-sacha_ideling')}
												       src={'/Global/sacha_idleing_v2.png'}
												       alt={"Sacha Idleing"}
												       width={64}
												       height={80}
												       unoptimized={true}
												       priority={true}
												/>
												:
												<Image className={cn(styles.idleing, {[styles.current]: sachaState === SachaState.IDLEING}, 'gsap-sacha_ideling')}
												       src={'/images/Game/Sacha/idleing_gif_v3.png'}
												       alt={"Sacha Idleing"}
												       width={64}
												       height={80}
												       unoptimized={true}
												       priority={true}
												/>) :
										<div className={cn(styles.idleing, {[styles.current]: sachaState === SachaState.IDLEING}, 'gsap-sacha_ideling')}/>
								}
								<Image className={cn(styles.walking, {[styles.current]: sachaState === SachaState.WALKING}, 'gsap-sacha_walking')}
								       src={'/images/Game/Sacha/walking_gif_v4.png'}
								       alt={"Sacha Walking"}
								       width={64}
								       height={80}
								       unoptimized={true}
								       priority={true}
								/>
								<Image className={cn(styles.jumping, {[styles.current]: sachaState === SachaState.JUMPING}, 'gsap-sacha_jumping')}
								       src={'/images/Game/Sacha/jumping_gif_v3.png'}
								       alt={"Sacha Jumping"}
								       width={64}
								       height={80}
								       unoptimized={true}
								       priority={true}
								/>
							</div>
						</div>
						{/*endregion*/}
					</div>
					{/*endregion*/}

					{/*Progress Bar - Timeline*/}
					<Timeline className={cn(styles.timeline, 'timeline')}/>

					{/*Sound*/}
					<SoundButton/>

					<Bag className={styles.bag}/>
				</div>
			</section>
	)
}
export default Game;
